import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, AlertTriangle, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const CONSENT_ITEMS = [
  { k: "privacy", label: "Consenso per la privacy" },
  { k: "communications", label: "Consenso invio comunicazioni" },
  { k: "ts_data", label: "Invio dati al Sistema TS" },
];

export const ConsentRow = ({ ok, label, alert }) => (
  <div className="flex items-center gap-2 text-[13px] text-slate-700">
    {alert
      ? <AlertTriangle className="h-4 w-4 text-amber-500" />
      : ok ? <Check className="h-4 w-4 text-emerald-600 stroke-[3]" /> : <X className="h-4 w-4 text-red-600 stroke-[3]" />}
    <span>{label}</span>
  </div>
);

export function usePatientConsents(fiscalCode) {
  const [consents, setConsents] = useState(null);
  useEffect(() => {
    if (!fiscalCode) return;
    api.get("/patients/consents", { params: { fiscal_code: fiscalCode } })
      .then((r) => setConsents(r.data.consents)).catch(() => setConsents(null));
  }, [fiscalCode]);
  const save = async (patch) => {
    try {
      const { data } = await api.put("/patients/consents", { fiscal_code: fiscalCode, ...patch });
      setConsents(data.consents); toast.success("Consensi aggiornati");
    } catch { toast.error("Salvataggio non riuscito"); }
  };
  return { consents, save };
}

export const ConsentsSummary = ({ consents }) => (
  <div className="space-y-1" data-testid="patient-consents-summary">
    {CONSENT_ITEMS.map((c) => <ConsentRow key={c.k} ok={!!consents?.[c.k]} label={c.label} />)}
    {consents?.anamnesis_alert && <ConsentRow alert label={`Alert anamnesi: ${consents.anamnesis_alert}`} />}
  </div>
);

export const ConsentsEditor = ({ consents, onSave }) => {
  const [alert, setAlert] = useState(consents?.anamnesis_alert || "");
  useEffect(() => { setAlert(consents?.anamnesis_alert || ""); }, [consents?.anamnesis_alert]);
  return (
    <div className="space-y-3" data-testid="patient-consents-editor">
      {CONSENT_ITEMS.map((c) => (
        <label key={c.k} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 cursor-pointer">
          <span className="text-sm text-slate-700">{c.label}</span>
          <span className="flex items-center gap-2">
            {consents?.[c.k] ? <Check className="h-4 w-4 text-emerald-600 stroke-[3]" /> : <X className="h-4 w-4 text-red-600 stroke-[3]" />}
            <input type="checkbox" checked={!!consents?.[c.k]} onChange={(e) => onSave({ [c.k]: e.target.checked })}
                   className="h-4 w-4 accent-[#1E88E5]" data-testid={`consent-${c.k}`} />
          </span>
        </label>
      ))}
      <div className="flex items-end gap-2 pt-1">
        <div className="flex-1">
          <label className="text-xs text-slate-500 flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-500" /> Alert anamnesi (allergie, note critiche)</label>
          <Input value={alert} onChange={(e) => setAlert(e.target.value)} placeholder="Es. Allergia penicillina" data-testid="consent-anamnesis-alert" className="h-9 bg-white" />
        </div>
        <Button size="sm" className="h-9 bg-[#1E88E5] hover:bg-[#1976D2] text-white" onClick={() => onSave({ anamnesis_alert: alert })} data-testid="consent-alert-save">
          <Pencil className="h-3.5 w-3.5 mr-1" /> Salva
        </Button>
      </div>
    </div>
  );
};
